import Logger from "../lib/logger"
// Push Subscription Manager
// Handles Service Worker registration and push notification subscriptions

export default class PushSubscriptionManager {
  constructor(options = {}) {
    this.vapidPublicKey = options.vapidPublicKey || null
    this.serviceWorkerPath = options.serviceWorkerPath || '/service-worker.js'
    this.subscriptionEndpoint = options.subscriptionEndpoint || '/api/push_subscriptions'
    this.registration = null
    this.subscription = null
  }

  // ========================================
  // SERVICE WORKER REGISTRATION
  // ========================================

  async register() {
    if (!('serviceWorker' in navigator)) {
      Logger.debug('[PushManager] Service Workers not supported')
      return false
    }

    if (!('PushManager' in window)) {
      Logger.debug('[PushManager] Push notifications not supported')
      return false
    }

    try {
      this.registration = await navigator.serviceWorker.register(this.serviceWorkerPath)
      Logger.debug('[PushManager] Service Worker registered:', this.registration.scope)

      // Wait for service worker to be ready
      await navigator.serviceWorker.ready
      Logger.debug('[PushManager] Service Worker is ready')

      // Send VAPID key to service worker
      if (this.vapidPublicKey) {
        this.sendMessageToServiceWorker({
          type: 'SET_VAPID_KEY',
          key: this.vapidPublicKey
        })
      }

      return true
    } catch (error) {
      Logger.error('[PushManager] Service Worker registration failed:', error)
      return false
    }
  }

  async unregister() {
    if (this.registration) {
      try {
        await this.registration.unregister()
        Logger.debug('[PushManager] Service Worker unregistered')
        this.registration = null
        return true
      } catch (error) {
        Logger.error('[PushManager] Failed to unregister Service Worker:', error)
        return false
      }
    }
    return true
  }

  // ========================================
  // PUSH SUBSCRIPTION
  // ========================================

  async subscribe() {
    if (!this.registration) {
      Logger.debug('[PushManager] No service worker registration')
      return null
    }

    // Check current permission
    const permission = await Notification.requestPermission()
    if (permission !== 'granted') {
      Logger.debug('[PushManager] Notification permission denied')
      return null
    }

    try {
      // Get existing subscription or create new one
      this.subscription = await this.registration.pushManager.getSubscription()

      if (!this.subscription) {
        const options = {
          userVisibleOnly: true,
          applicationServerKey: this.urlBase64ToUint8Array(this.vapidPublicKey)
        }

        this.subscription = await this.registration.pushManager.subscribe(options)
        Logger.debug('[PushManager] New push subscription created')
      } else {
        Logger.debug('[PushManager] Existing push subscription found')
      }

      // Send subscription to server
      await this.sendSubscriptionToServer(this.subscription)

      return this.subscription
    } catch (error) {
      Logger.error('[PushManager] Failed to subscribe:', error)
      return null
    }
  }

  async unsubscribe() {
    if (!this.subscription) {
      this.subscription = await this.getSubscription()
    }

    if (this.subscription) {
      try {
        await this.subscription.unsubscribe()
        await this.removeSubscriptionFromServer(this.subscription)
        Logger.debug('[PushManager] Unsubscribed from push notifications')
        this.subscription = null
        return true
      } catch (error) {
        Logger.error('[PushManager] Failed to unsubscribe:', error)
        return false
      }
    }

    return true
  }

  async getSubscription() {
    if (!this.registration) {
      this.registration = await navigator.serviceWorker.ready
    }

    if (this.registration) {
      return await this.registration.pushManager.getSubscription()
    }

    return null
  }

  isSubscribed() {
    return this.subscription !== null
  }

  // ========================================
  // SERVER COMMUNICATION
  // ========================================

  async sendSubscriptionToServer(subscription) {
    try {
      const response = await fetch(this.subscriptionEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCsrfToken()
        },
        body: JSON.stringify({
          subscription: {
            endpoint: subscription.endpoint,
            p256dh_key: this.arrayBufferToBase64(subscription.getKey('p256dh')),
            auth_key: this.arrayBufferToBase64(subscription.getKey('auth')),
            user_agent: navigator.userAgent
          }
        })
      })

      if (response.ok) {
        Logger.debug('[PushManager] Subscription saved to server')
        return true
      } else {
        Logger.warn('[PushManager] Failed to save subscription to server')
        return false
      }
    } catch (error) {
      Logger.error('[PushManager] Error saving subscription:', error)
      return false
    }
  }

  async removeSubscriptionFromServer(subscription) {
    try {
      const response = await fetch(this.subscriptionEndpoint, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCsrfToken()
        },
        body: JSON.stringify({
          endpoint: subscription.endpoint
        })
      })

      if (response.ok) {
        Logger.debug('[PushManager] Subscription removed from server')
        return true
      } else {
        Logger.warn('[PushManager] Failed to remove subscription from server')
        return false
      }
    } catch (error) {
      Logger.error('[PushManager] Error removing subscription:', error)
      return false
    }
  }

  // ========================================
  // DIRECT NOTIFICATION (for testing)
  // ========================================

  async showNotification(title, options = {}) {
    if (!this.registration) {
      this.registration = await navigator.serviceWorker.ready
    }

    if (this.registration) {
      const defaultOptions = {
        icon: '/assets/logo.png',
        badge: '/assets/badge.png',
        vibrate: [200, 100, 200]
      }

      await this.registration.showNotification(title, { ...defaultOptions, ...options })
      return true
    }

    return false
  }

  // Send message to service worker
  sendMessageToServiceWorker(message) {
    if (this.registration && this.registration.active) {
      this.registration.active.postMessage(message)
    } else if (navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage(message)
    }
  }

  // ========================================
  // UTILITY METHODS
  // ========================================

  getCsrfToken() {
    return document.querySelector('[name="csrf-token"]')?.content || ''
  }

  // Convert VAPID key from base64 to Uint8Array
  urlBase64ToUint8Array(base64String) {
    if (!base64String) return null

    const padding = '='.repeat((4 - base64String.length % 4) % 4)
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/')

    const rawData = window.atob(base64)
    const outputArray = new Uint8Array(rawData.length)

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i)
    }

    return outputArray
  }

  // Convert ArrayBuffer to base64
  arrayBufferToBase64(buffer) {
    if (!buffer) return null

    const bytes = new Uint8Array(buffer)
    let binary = ''
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i])
    }
    return window.btoa(binary)
  }

  // ========================================
  // STATUS CHECKING
  // ========================================

  async checkStatus() {
    const status = {
      serviceWorkerSupported: 'serviceWorker' in navigator,
      pushManagerSupported: 'PushManager' in window,
      notificationSupported: 'Notification' in window,
      notificationPermission: Notification.permission,
      serviceWorkerRegistered: false,
      pushSubscribed: false
    }

    if (status.serviceWorkerSupported) {
      const registrations = await navigator.serviceWorker.getRegistrations()
      status.serviceWorkerRegistered = registrations.length > 0
    }

    const subscription = await this.getSubscription()
    status.pushSubscribed = subscription !== null

    return status
  }

  // Check if push notifications are available
  isAvailable() {
    return 'serviceWorker' in navigator && 
           'PushManager' in window && 
           'Notification' in window
  }

  // Check if push notifications are permitted
  isPermitted() {
    return Notification.permission === 'granted'
  }
}

// Export singleton instance
export const pushManager = new PushSubscriptionManager()

// Auto-initialize if vapid key is available in meta tag
document.addEventListener('DOMContentLoaded', () => {
  const vapidKeyMeta = document.querySelector('meta[name="vapid-public-key"]')
  if (vapidKeyMeta) {
    pushManager.vapidPublicKey = vapidKeyMeta.content
    pushManager.register().then(() => {
      Logger.debug('[PushManager] Auto-initialized from meta tag')
    })
  }
})